import React from 'react';
import clsx from 'clsx';
import useStyles from './styles';
import { PostType } from 'types/post';
import * as IconMDI from 'mdi-material-ui';
import { ROUTES } from 'config/Nav';
import { BaseLink } from '@elevator/ui';

/**
 * Props for the ContentLabel component.
 * Renders a small, styled link indicating the type of a post.
 */
export type ContentLabelPropTypes = {
  /** The post type label (e.g. 'Submitted', 'Premiere', 'Paid', etc.). */
  type: PostType;
  /**
   * Optional sponsor name used only when `type` is 'Sponsored'.
   * When provided, the link will render as "by {sponsoredBy}".
   */
  sponsoredBy?: string;
};

/**
 * Renders a badge-like link for a given post type.
 *
 * - Chooses the target URL and whether the link is external based on `type`
 * - Applies background/text styles for different post types
 * - For `Sponsored` posts, appends "by {sponsoredBy}" when provided
 * - Only rendered when post type is not 'Standard' (caller guards). Featured renders "FEATURED" badge.
 */
const ContentLabel = (props: ContentLabelPropTypes) => {
  const { type, sponsoredBy } = props;
  const { classes } = useStyles();

  const className = [classes.link];
  let textContent: string = type;
  let href = ROUTES.SUBMIT.to;
  let external = ROUTES.SUBMIT.external;

  switch (type) {
    case 'Submitted':
      className.push(classes.blueBackground);
      className.push(classes.whiteText);
      href = ROUTES.TYPE.to + 'submissions';
      break;
    case 'Premiere':
      href = ROUTES.TYPE.to + 'premiered';
      className.push(classes.yellowBackground);
      className.push(classes.blackText);
      break;
    case 'Paid':
      href = ROUTES.CONTACT.to;
      external = ROUTES.CONTACT.external;
      textContent += ' Placement';
      break;
    case 'Sponsored':
      {
        sponsoredBy ? (href = ROUTES.SUBMIT.to) : (href = ROUTES.TYPE.to + 'sponsored');
      }
      className.push(classes.blackBackground);
      className.push(classes.whiteText);
      break;
    case 'Promoted':
      textContent = 'Promoted';
      href = ROUTES.TYPE.to + 'promoted';
      className.push(classes.blackBackground);
      className.push(classes.whiteText);
      break;
    case 'Featured':
      textContent = 'FEATURED';
      href = ROUTES.TYPE.to + 'featured';
      className.push(classes.blackBackground);
      className.push(classes.whiteText);
      break;
    default:
      return null;
  }

  return (
    <BaseLink
      external={external}
      href={href}
      className={clsx(className)}
      data-component="ContentLabel"
    >
      <span className={classes.badgeContent}>
        {type === 'Premiere' && <IconMDI.Star classes={{ root: classes.icon }} />}
        <span className={classes.labelContent}>
          {textContent}
          {type === 'Sponsored' && sponsoredBy && <> by {sponsoredBy}</>}
        </span>
      </span>
    </BaseLink>
  );
};

export default ContentLabel;
